import { batch } from 'react-redux';

import { DataLakeEnums } from 'dashboard-services/api/dataLake';
import { CommonService } from 'dashboard-services/services';
import { globalActions, markdownEditorActions, statusBoxActions } from 'primary-components';

import { codeEditorActions, csvEditorActions, ftlEditorActions, htmlEditorActions, imagePreviewActions, pdfViewerActions } from 'actions/modules/tools';

import { csvBasicEditorActions, pythonEditorActions } from '../tools';

export const MAX_CSV_SIZE_MB = 20
export const MAX_JSON_SIZE_MB = 25
export const MAX_CSV_BASIC_SIZE_MB = 250

export default(() => {
  const DATALAKE_LAYOUT = {
    VERSIONS: "VERSIONS",
    LATEST: "LATEST"
  }

  const APPROVALS_LIST_VIEW = {
    VERSIONS: 'VERSIONS',
    SECTIONS: 'SECTIONS'
  }

  const downloadFile = ({ id, newTab = false, getFileMetadataCall }) => dispatch => 
    getFileMetadataCall({ id })
      .then(response => {
        const location = response.headers.get("Location");
        if (location) {
          CommonService.openInNewTab(location, newTab);
        } else {
          dispatch(globalActions.getMessageStore()).error("Internal server error.")
        }
      })

  const openHtmlEditor = ({ fid, provider, disabled = Boolean(provider) }) => dispatch => {
          batch(() => {
            dispatch(htmlEditorActions.changeValue(true, 'open'))
            dispatch(htmlEditorActions.changeValue(fid, 'fileId'))
            dispatch(htmlEditorActions.changeValue(provider, 'provider'))
            dispatch(htmlEditorActions.changeValue(disabled, 'disabled'))
          })
        },
        openFtlEditor = ({ fid, provider, disabled = Boolean(provider) }) => dispatch => {
          batch(() => {
            dispatch(ftlEditorActions.changeValue(true, 'open'))
            dispatch(ftlEditorActions.changeValue(fid, 'fileId'))
            dispatch(ftlEditorActions.changeValue(provider, 'provider'))
            dispatch(ftlEditorActions.changeValue(disabled, 'disable'))
          })
        },
        openCodeEditor = ({ fid, provider, editable = !provider }) => dispatch => {
          batch(() => {
            dispatch(codeEditorActions.changeValue(fid, 'id'))
            dispatch(codeEditorActions.changeValue(true, 'open'))
            dispatch(codeEditorActions.changeValue(editable, 'editable'))
            dispatch(codeEditorActions.changeValue(provider ? provider : undefined, 'provider'))
          })
        },
        openPYEditor = ({ fid, jobId, provider, editable = !provider, taskId = undefined, onSubmitCallback = undefined }) => dispatch => {
          batch(() => {
            dispatch(pythonEditorActions.changeValue(fid, 'id'))
            dispatch(pythonEditorActions.changeValue(jobId, 'jobId'))
            dispatch(pythonEditorActions.changeValue(taskId, 'taskId'))
            dispatch(pythonEditorActions.changeValue(true, 'isOpen'))
            dispatch(pythonEditorActions.changeValue(editable, 'editable'))
            dispatch(pythonEditorActions.changeValue(provider ? provider : undefined, 'provider'))
            dispatch(pythonEditorActions.changeValue(onSubmitCallback, 'onSubmitCallback'))
          })
        },
        openCSVEditor = ({ fid, provider, editable = !provider }) => dispatch => {
          batch(() => {
            dispatch(csvEditorActions.changeValue(fid, 'id'))
            dispatch(csvEditorActions.changeValue(true, 'open'))
            dispatch(csvEditorActions.changeValue(editable, 'editable'))
            dispatch(csvEditorActions.changeValue(provider, 'provider'))
          })
        },
        openBasicCSVEditor = ({ fid, provider, editable = !provider }) => dispatch => {
          batch(() => {
            dispatch(csvBasicEditorActions.onChange(fid, 'id'))
            dispatch(csvBasicEditorActions.onChange(true, 'open'))
            dispatch(csvBasicEditorActions.onChange(editable, 'editable'))
            dispatch(csvBasicEditorActions.onChange(provider, 'provider'))
          })
        },
        openMarkdownEditor = ({ fid, fileName, groupName, provider, optionalText, disabled = Boolean(provider), withCallback = false }) => dispatch => {
          batch(() => {
            dispatch(markdownEditorActions.changeValue({ fid, fileName, groupName }, 'fileInfo'))
            dispatch(markdownEditorActions.changeValue(true, 'open'))
            dispatch(markdownEditorActions.changeValue(!disabled, 'editable'))
            dispatch(markdownEditorActions.changeValue(provider, 'provider'))
            dispatch(markdownEditorActions.changeValue(optionalText, 'optionalText'))
            dispatch(markdownEditorActions.changeValue(disabled, 'disabled'))
            if(withCallback) {
              dispatch(markdownEditorActions.changeValue(({ jobId }) => dispatch(statusBoxActions.addJob({ jobId, displayName: fileName, subtitle: groupName })), 'jobIdCallback'))
            }
          })
        },
        openPDFViewer = ({ fid, provider }) => dispatch => {
          batch(() => {
            dispatch(pdfViewerActions.changeValue(true, 'open'))
            dispatch(pdfViewerActions.changeValue(fid, 'fileId'))
            dispatch(pdfViewerActions.changeValue(provider, 'provider'))
          })
        },
        openImagePreview = ({ fid, provider }) => dispatch => {
          batch(() => {
            dispatch(imagePreviewActions.changeValue(true, 'isOpen'))
            dispatch(imagePreviewActions.changeValue(fid, 'fileId'))
            dispatch(imagePreviewActions.changeValue(provider, 'provider'))
          })
        }
  const isScript = parsedName => parsedName.endsWith(".sh") || parsedName.endsWith(".json") || parsedName.endsWith(".ipynb") || parsedName.endsWith(".txt") || parsedName.endsWith(".js") || parsedName.endsWith(".sql") || parsedName.endsWith(".xml")
  const isPython = parsedName => parsedName.endsWith(".py")
  const getMBSize = size => {
    if (size >= 0) {
      const sizeInKB = size/1000;
      if( sizeInKB < 1000) {
        return 1;
      } else {
        return Number.parseFloat(sizeInKB/1000)
      }
    } else {
      return 0
    }
  }
  const getFileEditAction = ({ fileName, fid, groupName, writeAccess, size, fileType, provider }) => dispatch => {
    const parsedName = String(fileName).toLowerCase().trim(),
          sizeInMB = getMBSize(size)

    if(parsedName.endsWith(".html") && writeAccess) {
      return {
        isEdit: true,
        onClick: () => dispatch(openHtmlEditor({ fid, provider })),
        text: "Edit html",
        shortText: "Edit",
        isUnknown: false
      }
    } else if(parsedName.endsWith(".ftl") && writeAccess) {
      return {
        isEdit: true,
        onClick: () => dispatch(openFtlEditor({ fid, provider })),
        text: "Edit ftl",
        shortText: "Edit",
        isUnknown: false
      }
    } else if((isScript(parsedName) || fileType === DataLakeEnums.FILE_TYPE.DATA_PREP) && writeAccess && sizeInMB > MAX_JSON_SIZE_MB) {
      return {
        isEdit: false,
        onClick: () => dispatch(globalActions.getMessageStore()).warning("File is  bigger than 25MB, please download a file instead."),
        text: "Edit file",
        shortText: "Edit",
        isUnknown: false
      }
    } else if(isPython(parsedName) && writeAccess) {
      return {
        isEdit: true,
        onClick: () => dispatch(openPYEditor({ fid, provider })),
        text: "Edit file",
        shortText: "Edit",
        isUnknown: false
      }
    } else if((isScript(parsedName) || fileType === DataLakeEnums.FILE_TYPE.DATA_PREP) && writeAccess) {
      return {
        isEdit: true,
        onClick: () => dispatch(openCodeEditor({ fid, provider })),
        text: "Edit file",
        shortText: "Edit",
        isUnknown: false
      }
    } else if(parsedName.endsWith(".csv") && writeAccess && sizeInMB < MAX_CSV_SIZE_MB) {
      return {
        isEdit: true,
        onClick: () => dispatch(openCSVEditor({ fid, provider })),
        text: "Edit csv",
        shortText: "Edit",
        isUnknown: false
      }
    } else if(parsedName.endsWith(".csv") && !writeAccess && sizeInMB < MAX_CSV_SIZE_MB) {
      return {
        isEdit: false,
        onClick: () => dispatch(openCSVEditor({ fid, provider, editable: false })),
        text: "Preview csv",
        shortText: "Preview",
        isUnknown: false
      }
    } else if(parsedName.endsWith(".csv") && writeAccess && sizeInMB >= MAX_CSV_SIZE_MB && sizeInMB < MAX_CSV_BASIC_SIZE_MB) {
      return {
        isEdit: true,
        onClick: () => dispatch(openBasicCSVEditor({ fid, provider })),
        text: "Edit csv (basic mode)",
        shortText: "Edit",
        isUnknown: false
      }
    } else if(parsedName.endsWith(".csv") && sizeInMB > MAX_CSV_BASIC_SIZE_MB) {
      return {
        isEdit: false,
        onClick: () => dispatch(openBasicCSVEditor({ fid, provider, editable: false })),
        text: "Preview csv (basic mode)",
        shortText: "Preview",
        isUnknown: false
      }
    } else if(parsedName.endsWith(".md") && writeAccess) {
      return {
        isEdit: true,
        onClick: () => dispatch(openMarkdownEditor({ fid, fileName, groupName, provider })),
        text: "Edit markdown",
        shortText: "Edit",
        isUnknown: false
      }
    } else if(parsedName.endsWith(".pdf")) {
      return {
        isEdit: false,
        onClick: () => dispatch(openPDFViewer({ fid, provider })),
        text: "Preview PDF",
        shortText: "Preview",
        isUnknown: false
      }
    } else if(CommonService.isSupportedImage(parsedName)) {
      return {
        isEdit: false,
        onClick: () => dispatch(openImagePreview({ fid, provider })),
        text: "Preview Image",
        shortText: "Preview",
        isUnknown: false
      }
    }
    return {
      isUnknown: true
    }
  }

  const openEditorBasedOnFile = (file, { searchFilesCall, getFileSizeCall, dynamic, provider, taskId, jobId, onSubmitCallback = undefined } = {}) => dispatch => {
    let action
    const parsedName = String(file?.name).toLowerCase()
    if(parsedName.endsWith(".pdf")) {
      action = id => dispatch(openPDFViewer({ fid: id, provider }))
    } else if(parsedName.endsWith(".ftl")) {
      action = id => dispatch(openFtlEditor({ fid: id, provider, disabled: Boolean(provider) || dynamic || file?.id !== undefined }))
    } else if(parsedName.endsWith(".html")) {
      action = id => dispatch(openHtmlEditor({ fid: id, provider, disabled: Boolean(provider) || dynamic || file?.id !== undefined }))
    } else if(parsedName.endsWith(".md")) {
      action = id => dispatch(openMarkdownEditor({ fid: id, fileName: file.name, groupName: file.groupName, provider, optionalText: file.name, disabled: Boolean(provider) || dynamic || file?.id !== undefined, withCallback: false }))
    } else if(parsedName.endsWith(".csv")) {
      action = (id, size) => {
        const fileSize = 
        size ? Promise.resolve(size) : 
          getFileSizeCall({ id })
            .then(res => getMBSize(res?.size))

        fileSize.then(res => {
          if(res < MAX_CSV_SIZE_MB) {
            dispatch(openCSVEditor({ fid: id, provider, editable: !provider && !dynamic && file?.id === undefined }))
          } else {
            dispatch(openBasicCSVEditor({ fid: id, provider, editable: !provider && !dynamic && file?.id === undefined && res < MAX_CSV_BASIC_SIZE_MB }))
          }
        })
      }
    } else if(isPython(parsedName)) {
      action = (id, size) => {
        const fileSize = 
        size ? Promise.resolve(size) : 
          getFileSizeCall({ id })
            .then(res => getMBSize(res?.size))

        fileSize.then(res => {
          if(res < MAX_JSON_SIZE_MB) {
            dispatch(openPYEditor({ fid: id, jobId, provider, editable: !provider && !dynamic && file?.id === undefined, taskId, onSubmitCallback }))
          } else {
            dispatch(globalActions.getMessageStore()).warning("File is  bigger than 25MB, please download a file instead.")
          }
        })
      }
    } else if(isScript(parsedName)) {
      action = (id, size) => {
        const fileSize = 
        size ? Promise.resolve(size) : 
          getFileSizeCall({ id })
            .then(res => getMBSize(res?.size))

        fileSize.then(res => {
          if(res < MAX_JSON_SIZE_MB) {
            dispatch(openCodeEditor({ fid: id, provider, editable: !provider && !dynamic && file?.id === undefined }))
          } else {
            dispatch(globalActions.getMessageStore()).warning("File is  bigger than 25MB, please download a file instead.")
          }
        })
      }
    } else if(CommonService.isSupportedImage(parsedName)) {
      action = id => dispatch(openImagePreview({ fid: id, provider }))
    }
  
    if(file?.id) {
      action(file.id)
    } else {
      searchFilesCall()
        .then(response => {
          const items = response?.data?.items || []
          if(items?.[0]) {
            const fileSize = getMBSize(items[0].size)
            if(fileSize < 20) {
              action(items[0].fid)
            } else {
              dispatch(globalActions.getToastsStore()).info(
                "File is  bigger than 20MB, preview can take some time. Are you sure you want to proceed?",
                "Preview warning",
                {
                  withCancel: false,
                  confirmText: "Preview",
                  confirmAction: () => action(items[0].fid, fileSize)
                }
              );
            }
          } else {
            dispatch(globalActions.getMessageStore()).error("No file found at the moment of task execution.")
          }
        })
    }
  }

  return {
    downloadFile,
    DATALAKE_LAYOUT,
    APPROVALS_LIST_VIEW,
    getFileEditAction,
    openEditorBasedOnFile
  }
})()
